import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Card,
  Button,
  Space,
  Tooltip,
  Switch,
  Select,
  Input,
  Skeleton,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlusOutlined,
  FilterOutlined,
  LockOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { capitalize, startCase } from "lodash";
import { getRoleClassName, getRoleColors } from "../../utils/roleColors";
import { resetUserPassword } from "../../../../services/userService";
import { getAssignableRoles } from "../../utils/userHelpers";
import { canAssignRole, canManageUser } from "../../../../utils/roleHelpers";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import { ResultsLoadMorePanel } from "../../../../shared";
import UserTableSkeleton from "./UserTableSkeleton";
import ConfirmModal from "../Modal/ConfirmModal";
import PasswordResetModal from "../Modal/PasswordResetModal";
import ColumnFilter from "../Filters/ColumnFilter";
import {
  DEBOUNCE_DELAY,
  INITIAL_MODAL_STATE,
  STATUS_OPTIONS,
} from "../../constants/userConstants";
import "./UserTable.scss";

const { Option } = Select;

const UserTable = ({
  currentUser,
  users,
  loading,
  onEditUser,
  onDeleteUser,
  onToggleStatus,
  onChangeRole,
  onRefresh,
  onCreateUser,
  isLoadingMore,
  hasMore,
  onLoadMore,
  totalUsers,
  activeFilters = {},
  allCompanies = [],
  companiesLoading = false,
  allRoles = [],
  onFilterChange,
  sortOrder = 'asc',
  onSortChange,
  permissions = {
    create: false,
    update: false,
    delete: false,
    reset_password: false,
  },
}) => {
  const message = useMessageApi();
  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingStatusEmail, setUpdatingStatusEmail] = useState(null);
  const [updatingRoleEmail, setUpdatingRoleEmail] = useState(null);
  const [confirmModal, setConfirmModal] = useState(INITIAL_MODAL_STATE);
  const [passwordResetModal, setPasswordResetModal] =
    useState(INITIAL_MODAL_STATE);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  // Refs
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const isClearingFilters = useRef(false);
  const activeFiltersRef = useRef(activeFilters);
  const onFilterChangeRef = useRef(onFilterChange);

  // Update refs
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
    onFilterChangeRef.current = onFilterChange;
  }, [activeFilters, onFilterChange]);

  // Helper: Check if current user is restricted from managing the target user
  // Uses role_order: A user can only manage users whose roles have a higher role_order (lower privilege)
  const isHierarchyRestricted = useCallback(
    (user) => !canManageUser(currentUser, user, allRoles),
    [currentUser, allRoles]
  );

  // Memoized filter options
  const filterOptions = useMemo(
    () => ({
      companies:
        allCompanies.length > 0
          ? allCompanies
          : [...new Set(users.map((user) => user.company))]
              .filter(Boolean)
              .sort(),
      roles: getAssignableRoles(allRoles, currentUser).map((role) => ({
        value: Number(role.id),
        label: startCase(role.name),
      })),
      statuses: STATUS_OPTIONS,
    }),
    [allCompanies, allRoles, users, currentUser]
  );

  // Memoized current filters
  const currentFilters = useMemo(
    () => ({
      companies: activeFilters.company ? [activeFilters.company] : [],
      roles: activeFilters.role_id
        ? Array.isArray(activeFilters.role_id)
          ? activeFilters.role_id
          : [activeFilters.role_id]
        : [],
      statuses:
        activeFilters.is_active !== undefined ? [activeFilters.is_active] : [],
    }),
    [activeFilters]
  );

  // Memoized computed values
  const hasActiveFilters = useMemo(
    () => Object.keys(activeFilters).length > 0 || searchTerm.trim().length > 0,
    [activeFilters, searchTerm]
  );

  const activeFiltersCount = useMemo(
    () =>
      currentFilters.companies.length +
      currentFilters.roles.length +
      currentFilters.statuses.length +
      (searchTerm.trim() ? 1 : 0),
    [currentFilters, searchTerm]
  );

  // Debounced search
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isClearingFilters.current) {
      isClearingFilters.current = false;
      return;
    }

    clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      const newFilters = { ...activeFiltersRef.current };
      searchTerm.trim()
        ? (newFilters.search = searchTerm.trim())
        : delete newFilters.search;
      onFilterChangeRef.current?.(newFilters);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm]);

  // Handlers - optimized with useCallback
  const handleFilterChange = useCallback(
    (column, values) => {
      const newFilters = { ...activeFilters };
      const filterMap = {
        companies: { key: "company", value: values[0] },
        roles: { key: "role_id", value: values },
        statuses: { key: "is_active", value: values[0] },
      };

      const filter = filterMap[column];
      if (filter) {
        values.length > 0
          ? (newFilters[filter.key] = filter.value)
          : delete newFilters[filter.key];
      }

      onFilterChange?.(newFilters);
    },
    [activeFilters, onFilterChange]
  );

  const clearFilters = useCallback(async () => {
    isClearingFilters.current = true;
    setSearchTerm("");
    await Promise.resolve(onFilterChange?.({}));
  }, [onFilterChange]);

  const handleRefreshClick = useCallback(async () => {
    if (hasActiveFilters) {
      await clearFilters();
    } else {
      await Promise.resolve(onRefresh?.());
    }
  }, [hasActiveFilters, clearFilters, onRefresh]);

  const handleStatusToggle = useCallback(
    async (userEmail, currentStatus) => {
      // Check permission before toggling status
      if (!permissions.update) {
        message.error("You don't have permission to update user status");
        return;
      }
      
      setUpdatingStatusEmail(userEmail);
      try {
        await onToggleStatus(userEmail, !currentStatus);
      } finally {
        setUpdatingStatusEmail(null);
      }
    },
    [onToggleStatus, permissions.update]
  );

  const handleRoleChange = useCallback(
    async (userEmail, newRoleId) => {
      // Check permission before changing role
      if (!permissions.update) {
        message.error("You don't have permission to update user roles");
        return;
      }
      
      setUpdatingRoleEmail(userEmail);
      try {
        await onChangeRole(userEmail, newRoleId);
      } finally {
        setUpdatingRoleEmail(null);
      }
    },
    [onChangeRole, permissions.update]
  );

  const handleOpenDeleteModal = useCallback((userId, userName) => {
    // Check permission before opening delete modal
    if (!permissions.delete) {
      message.error("You don't have permission to delete users");
      return;
    }
    setConfirmModal({ visible: true, userId, userName, userEmail: "" });
  }, [permissions.delete]);

  const handleConfirmDelete = useCallback(async () => {
    setDeletingUser(true);

    try {
      await onDeleteUser(confirmModal.userId);
      // Success message will be shown by hook
      setConfirmModal(INITIAL_MODAL_STATE);
    } catch (error) {
      console.error("Error deleting user:", error);
      message.error("Failed to delete user", 2.5);
    } finally {
      setDeletingUser(false);
    }
  }, [confirmModal.userId, onDeleteUser, message]);

  const handleOpenPasswordResetModal = useCallback(
    (userId, userName, userEmail) => {
      // Check permission before opening password reset modal
      if (!permissions.reset_password) {
        message.error("You don't have permission to reset user passwords");
        return;
      }
      setPasswordResetModal({ visible: true, userId, userName, userEmail });
    },
    [permissions.reset_password]
  );

  const handleConfirmPasswordReset = useCallback(
    async (newPassword) => {
      // Check permission before resetting password
      if (!permissions.reset_password) {
        message.error("You don't have permission to reset user passwords");
        setPasswordResetModal(INITIAL_MODAL_STATE);
        return;
      }

      setResettingPassword(true);

      try {
        const success = await resetUserPassword(
          passwordResetModal.userEmail,
          newPassword,
          currentUser?.id || null
        );
        
        if (success) {
          message.success(
            `Password reset successfully for ${passwordResetModal.userName}`,
            2.5
          );
          setPasswordResetModal(INITIAL_MODAL_STATE);
        } else {
          message.error("Failed to reset password", 2.5);
        }
      } catch (error) {
        console.error("Error resetting password:", error);
        message.error(error.message || "Failed to reset password", 2.5);
      } finally {
        setResettingPassword(false);
      }
    },
    [passwordResetModal, permissions, message]
  );


  // Render action button with tooltip and permission checks
  const renderActionButton = useCallback(
    (
      user,
      icon,
      onClick,
      title,
      restrictedTitle,
      btnClass = "",
      danger = false,
      hasPermission = true,
      permissionTooltip = "You don't have permission for this action"
    ) => {
      const isRestricted = isHierarchyRestricted(user);
      const isDisabled = isRestricted || !hasPermission;
      const restrictedTooltip =
        restrictedTitle || "You can only manage users with roles lower than your own";
      const tooltipText = isRestricted
        ? restrictedTooltip
        : !hasPermission
        ? permissionTooltip
        : title;

      return (
        <Tooltip
          title={tooltipText}
          placement="topLeft"
        >
          <Button
            type="text"
            icon={icon}
            onClick={onClick}
            disabled={isDisabled}
            className={`action-btn ${btnClass}`}
            danger={danger}
          />
        </Tooltip>
      );
    },
    [isHierarchyRestricted]
  );

  // Render empty state
  const renderEmptyState = () => {
    if (loading || users.length > 0) return null;

    return (
      <div className="empty-state">
        {hasActiveFilters ? (
          <>
            {searchTerm ? (
              <SearchOutlined className="empty-icon" />
            ) : (
              <FilterOutlined className="empty-icon" />
            )}
            <p>
              {searchTerm
                ? `No users match "${searchTerm}"`
                : "No users match the selected filters"}
            </p>
            <Button onClick={clearFilters} type="link">
              {searchTerm ? "Clear Search & Filters" : "Clear Filters"}
            </Button>
          </>
        ) : (
          <>
            <UserOutlined className="empty-icon" />
            <p>No users found</p>
          </>
        )}
      </div>
    );
  };

  return (
    <Card className="user-table-card">
      {/* Header */}
      <div className="table-header">
        <div className="table-title">
          <UserOutlined className="table-icon" />
          <span className="table-title-text">
            {loading ? (
              <Skeleton.Input
                active
                size="small"
                style={{ width: 120, height: 16 }}
              />
            ) : (
              `Users (${totalUsers || 0})`
            )}
          </span>
          <Tooltip 
            placement="topLeft"
            title={permissions.create ? "Add New User" : "You don't have permission to create users"}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateUser}
              disabled={!permissions.create}
              className="table-action-btn add-user-btn title-add-btn"
            />
          </Tooltip>
        </div>
        <div className="table-header-actions">
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            className="table-search-input"
          />
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="table-action-btn clear-filters-btn"
            >
              Clear Filters{" "}
              {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>

            <Tooltip title="Refresh Users">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshClick}
                loading={loading}
                className="table-action-btn"
              />
            </Tooltip>
          </Space>
        </div>
      </div>

      {/* Table */}
      <div className="user-table-container">
        <table className="custom-user-table">
          <thead className="sticky-header">
            <tr>
              <th>
                <ColumnFilter
                  label="Status"
                  options={filterOptions.statuses}
                  selectedValues={currentFilters.statuses}
                  onChange={(values) => handleFilterChange("statuses", values)}
                />
              </th>
              <th 
                className="sortable-header"
                onClick={onSortChange}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>Full Name</span>
                  {sortOrder === 'asc' ? (
                    <ArrowUpOutlined style={{ fontSize: '11px', color: '#52c41a' }} />
                  ) : (
                    <ArrowDownOutlined style={{ fontSize: '11px', color: '#52c41a' }} />
                  )}
                </div>
              </th>
              <th>Email</th>
              <th>
                <ColumnFilter
                  label="Company"
                  options={filterOptions.companies}
                  selectedValues={currentFilters.companies}
                  onChange={(values) => handleFilterChange("companies", values)}
                  loading={companiesLoading}
                />
              </th>
              <th>
                <ColumnFilter
                  label="Role"
                  options={filterOptions.roles}
                  selectedValues={currentFilters.roles}
                  onChange={(values) => handleFilterChange("roles", values)}
                />
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 && <UserTableSkeleton rows={10} />}

            {users.map((user) => {
              const isRestricted = isHierarchyRestricted(user);
              const roleColors = getRoleColors(user.role);

              return (
                <tr key={user.id} className="user-row">
                  <td>
                    <div className="status-toggle-wrapper">
                      <Tooltip
                        title={
                        isRestricted
                          ? "You can only manage users with roles lower than your own"
                            : !permissions.update
                            ? "You don't have permission to update user status"
                            : user.is_active
                            ? "Click to deactivate user"
                            : "Click to activate user"
                        }
                        placement="top"
                      >
                        <Switch
                          checked={user.is_active}
                          onChange={() =>
                            handleStatusToggle(user.email, user.is_active)
                          }
                          loading={updatingStatusEmail === user.email}
                          disabled={isRestricted || !permissions.update}
                          checkedChildren="Active"
                          unCheckedChildren="Inactive"
                          className="status-toggle"
                        />
                      </Tooltip>
                    </div>
                  </td>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        <UserOutlined />
                      </div>
                      <div className="user-name">{user.name}</div>
                    </div>
                  </td>
                  <td className="user-email">{user.email}</td>
                  <td>
                    <div className="company-info">
                      <TeamOutlined className="company-icon" />
                      <span>{user.company}</span>
                    </div>
                  </td>
                  <td>
                    <Tooltip
                      title={
                        isRestricted
                          ? "You can only manage users with roles lower than your own"
                          : !permissions.update
                          ? "You don't have permission to update user roles"
                          : capitalize(user.role)
                      }
                      placement="top"
                    >
                      <Select
                        value={user.role_id ? Number(user.role_id) : undefined}
                        onChange={(newRoleId) =>
                          handleRoleChange(user.email, newRoleId)
                        }
                        loading={updatingRoleEmail === user.email}
                        disabled={isRestricted || !permissions.update}
                        placeholder="Select Role"
                        className={`role-selector role-${getRoleClassName(
                          user.role
                        )}${isRestricted ? " role-protected" : ""}`}
                        classNames={{
                          popup: { root: "role-selector-dropdown" },
                        }}
                        size="small"
                        popupMatchSelectWidth={false}
                        variant="borderless"
                        title=""
                        style={{
                          "--role-primary": roleColors.primary,
                          "--role-light": roleColors.light,
                          "--role-shadow": roleColors.shadow,
                        }}
                      >
                        {allRoles
                          .filter((role) => {
                            const roleId = Number(role.id);
                            const isCurrentRole = Number(user.role_id) === roleId;
                            const isAssignable = canAssignRole(
                              currentUser,
                              roleId,
                              allRoles
                            );
                            // Show only current role or assignable roles
                            return isCurrentRole || isAssignable;
                          })
                          .map((role) => {
                            const roleId = Number(role.id);
                            const isCurrentRole = Number(user.role_id) === roleId;
                            const isAssignable = canAssignRole(
                              currentUser,
                              roleId,
                              allRoles
                            );
                            // Current role is only disabled if user is restricted, assignable roles are never disabled
                            const isDisabled = isCurrentRole && isRestricted;

                            return (
                              <Option
                                key={role.id}
                                value={roleId}
                                disabled={isDisabled}
                              >
                                {startCase(role.name)}
                              </Option>
                            );
                          })}
                      </Select>
                    </Tooltip>
                  </td>
                  <td>
                    <Space size="small" className="action-buttons">
                      {renderActionButton(
                        user,
                        <EditOutlined />,
                        () => onEditUser(user),
                        "Edit User",
                        "You can only manage users with roles lower than your own",
                        "edit-btn",
                        false,
                        permissions.update,
                        "You don't have permission to edit users"
                      )}
                      {renderActionButton(
                        user,
                        <LockOutlined />,
                        () =>
                          handleOpenPasswordResetModal(
                            user.id,
                            user.name,
                            user.email
                          ),
                        "Reset Password",
                        "You can only manage users with roles lower than your own",
                        "reset-btn",
                        false,
                        permissions.reset_password,
                        "You don't have permission to reset passwords"
                      )}
                      {renderActionButton(
                        user,
                        <DeleteOutlined />,
                        () => handleOpenDeleteModal(user.id, user.name),
                        "Delete User from System",
                        "You can only manage users with roles lower than your own",
                        "delete-btn",
                        true,
                        permissions.delete,
                        "You don't have permission to delete users"
                      )}
                    </Space>
                  </td>
                </tr>
              );
            })}

            {isLoadingMore && <UserTableSkeleton rows={8} />}
          </tbody>
        </table>

        {renderEmptyState()}
      </div>

      {/* Load More Panel */}
      <ResultsLoadMorePanel
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={(shouldScroll) => {
          if (onLoadMore) {
            onLoadMore(shouldScroll);
            // Auto scroll down to show loading area if shouldScroll is true
            if (shouldScroll) {
              setTimeout(() => {
                window.scrollTo({
                  top: document.documentElement.scrollHeight,
                  behavior: 'smooth'
                });
              }, 100);
            }
          }
        }}
        loading={loading}
        itemsCount={users.length}
        shouldScroll={true}
        forceDisableButton={hasActiveFilters}
      />

      {/* Modals */}
      <ConfirmModal
        visible={confirmModal.visible}
        title="Delete User"
        message={`Are you sure you want to delete ${confirmModal.userName}? They will no longer be able to access the system.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmModal(INITIAL_MODAL_STATE)}
        loading={deletingUser}
        confirmText="Yes, Delete User"
        cancelText="Cancel"
        type="danger"
      />

      <PasswordResetModal
        visible={passwordResetModal.visible}
        userName={passwordResetModal.userName}
        userEmail={passwordResetModal.userEmail}
        onConfirm={handleConfirmPasswordReset}
        onCancel={() => setPasswordResetModal(INITIAL_MODAL_STATE)}
        loading={resettingPassword}
      />
    </Card>
  );
};

export default UserTable;
